import type { IANATimezones } from '@gcv/shared';
import { TaskCategory, TaskInternalType, TaskStatus } from '@gcv/shared';

import palette from 'ui/theme/palette';
import { DateTimeHelpers } from 'util/dateTime.util';
import { removeUuid } from 'util/format.util';
import { capitalizeVariables } from 'util/strings.util';
import type { TaskDetails, TaskDrawerField } from '../../tasks/tasks.model';
import { getTaskStatusLabel } from './get-task-status-label';

const getCompletedHowValue = (task: TaskDetails | null): string => {
  if (task?.internalType !== TaskInternalType.SEEDLING_QUESTIONNAIRE_COMPLETE) {
    return '--';
  }

  return task.groupedTasks?.[0]?.system_completed === false ? 'Manually Completed' : 'System Completed';
};

/** Fields shown in the drawer for a bank-facing (internal) task. */
export const getTaskDrawerFields = (
  task: TaskDetails | null,
  timezone: IANATimezones
): Record<string, TaskDrawerField> => ({
  name: {
    label: 'Task Name',
    value: task?.name ?? '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 100
  },
  status: {
    label: 'Status',
    value: getTaskStatusLabel(task),
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33,
    color: task?.status === TaskStatus.COMPLETE ? palette.primary.main : undefined
  },
  assignee: {
    label: 'Assignee',
    value: task?.assignee ?? '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  completedBy: {
    label: 'Completed By',
    value: task?.completedBy ?? '--',
    state: [TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  description: {
    label: 'Task Description',
    value: task ? removeUuid(task.description) : '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 100
  },
  lastReminder: {
    label: 'Last Reminder Sent On',
    value: DateTimeHelpers.formatISOToTableDateString(task?.lastReminderDate, timezone),
    state: [TaskStatus.OPEN],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 50
  },
  nextReminder: {
    label: 'Next Reminder Scheduled For',
    value: DateTimeHelpers.formatISOToTableDateString(task?.nextReminderDate, timezone),
    state: [TaskStatus.OPEN],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 50
  },
  account: {
    label: 'Account',
    value: task?.account ?? '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 100
  },
  associated: {
    label: 'Associated With',
    value:
      task?.associatedWith === 'fincen_reports'
        ? 'FinCEN Reports'
        : capitalizeVariables(task?.associatedWith ?? '') ?? '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 100
  },
  dueDate: {
    label: 'Due Date',
    value: DateTimeHelpers.formatISOToTableDateString(task?.dueDate, timezone) ?? '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  createdDate: {
    label: 'Created',
    value: DateTimeHelpers.formatISOToTableDateString(task?.createdDate, timezone) ?? '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  createdBy: {
    label: 'Created By',
    value: task?.createdBy ?? '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  completedDate: {
    label: 'Completed On',
    value: DateTimeHelpers.formatISOToTableDateString(task?.completedDate ?? '', timezone) ?? '--',
    state: [TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  completedHow: {
    label: 'Completed How',
    value: getCompletedHowValue(task),
    state: [TaskStatus.COMPLETE],
    type: [TaskCategory.SYSTEM],
    width: 33
  },
  note: {
    label: 'Completion Note',
    value: task?.completedNote?.length ? task.completedNote : '--',
    state: [TaskStatus.COMPLETE],
    type: [TaskCategory.USER],
    width: 100
  },
  systemNote: {
    label: 'Completion Note',
    value: task?.completedNote?.length ? task.completedNote : '--',
    state: [TaskStatus.COMPLETE],
    type: [TaskCategory.SYSTEM],
    width: 100
  }
});
